import Cookies from "js-cookie";
import React, { FunctionComponent, lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Get } from "../../utils/api";
import Navigation, { IChatMetadata, NavigationBarModel, NavigationModel } from "../navigation/NavigationNew";
import HealthBar from "./HealthBar";
import "./Layout.css";
import LightTheme from "./LightTheme";
import Maidchan, { BowReason, maidchanBow } from "./Maidchan";
import Page from "./Page";
import SessionRestored from "./SessionRestored";
import SunCalc from "suncalc";
import MaidchanBubbles from "./MaidchanBubbles";
import ClickEffects from "./ClickEffects";

const LazyLight = lazy(() => import(/* webpackChunkName: "LightTheme" */ "./LightTheme"));
const LazyDark = lazy(() => import(/* webpackChunkName: "DarkTheme" */ "./DarkTheme"));

const Layout: FunctionComponent = () => {
	const themeCookie = Cookies.get("color_theme") ?? "Light";
	const [derivedTheme, setDerivedTheme] = useState<"Light" | "Dark" | string>(themeCookie)
	const [theme, setTheme] = useState(themeCookie);
	const [time, setTime] = useState(new Date());
	const [pages, setPages] = useState<NavigationBarModel>();
	const [showTesla, setShowTesla] = useState<boolean>(Cookies.get("showTesla") === "true");

	const [loggedIn, setLoggedIn] = useState<boolean>();
	const [chatData, setChatData] = useState<IChatMetadata | null>(null);
	useEffect(() => { document.title = "メイドちゃん" }, []);
	const onLoggedIn = useCallback((value: IChatMetadata | boolean) => {
		setLoggedIn(!!value);
		if (!(typeof value == "boolean")) {
			setChatData(value);
		} else {
			setChatData(null);
		}
	}, [setLoggedIn]);
	const onLoggedOut = useCallback(() => { setLoggedIn(false); }, []);

	useEffect(() => {
		if (theme !== "Auto") {
			return setDerivedTheme(theme);
		}
		navigator.geolocation.getCurrentPosition((coords) => {
			// Browser is not allowed to get coords
			if (!coords) { 
				const hour = time.getHours();
				if (hour > 6 && hour < 20) {
					return setDerivedTheme("Light");
				}
				return setDerivedTheme("Dark");
			}

			const position = SunCalc.getPosition(time, coords.coords.latitude, coords.coords.longitude);
			if (position.altitude >= 0 && position.altitude <= Math.PI) {
				return setDerivedTheme("Light");
			}
			return setDerivedTheme("Dark");
		});
		const updateTime = setTimeout(() => setTime(new Date()), 60 * 1000);
		return () => clearTimeout(updateTime);
	}, [theme, time]);

	const renderTheme = useMemo(() => {
		if (derivedTheme === "Dark") {
			return <LazyDark />;
		}
		return <LazyLight />;
	}, [derivedTheme]);
	useEffect(() => {
		setTheme(Cookies.get("color_theme") ?? "Light");
	}, [loggedIn]);
	useEffect(() => {
		document.body.className = derivedTheme;
	}, [derivedTheme]);

	useEffect(() => {
		if (!loggedIn || pages?.pages.length === 0) { return; }
		maidchanBow(BowReason.Greeting);
	}, [loggedIn, pages?.pages.length]);

	const loadNavigation = useCallback(() => {
		Get<NavigationModel[]>("/layout/getNavigation")
			.then(result => setPages({
				pages: result,
				userImage: Date.now(),
			}));
		setShowTesla(Cookies.get("showTesla") === "true");
	}, [setPages]);

	return <>
		<Suspense fallback={<LightTheme />}>
			{renderTheme}
		</Suspense>
		<SessionRestored />
		<ClickEffects />
		<main>
			{/* <MaidchanBubbles /> */}
			{loggedIn && <ErrorBoundary fallback={<div />}><HealthBar logOut={onLoggedOut} /></ErrorBoundary>}
			<ErrorBoundary fallback={<div />}>
				<Navigation navigationBar={loggedIn ? pages : { pages: [], userImage: 1 }} loggedIn={loggedIn} showTesla={showTesla} setLoggedIn={onLoggedIn} loadNavigation={loadNavigation} />
			</ErrorBoundary>
			<Maidchan chatData={chatData} setChatData={setChatData} />
			<div className="main" style={{ padding: "0px" }}>
				<Page loggedIn={loggedIn} pages={loggedIn ? pages : { pages: [], userImage: 1 }} theme={[theme, setTheme]} refreshMenu={loadNavigation} />
			</div>
		</main>
	</>;
}

export default Layout;